import styled, { css } from "styled-components";
import WorldMap from "assets/homepage/landing/WorldMap.svg";
import { typography } from "screens/Homepage/components/typography";
import { SectionContainer, SectionTitle } from "screens/Homepage/styles";
import { Icon } from "@beamcloud/design-system";

const LaunchSection: React.FC = () => {
  return (
    <Container>
      <TitleWrapper>
        <StyledTitle>Run your entire gen AI stack on Beam</StyledTitle>
      </TitleWrapper>
      <CardGrid>
        <Card onClick={() => window.open("https://docs.beam.cloud/v2/endpoint/overview", "_blank")}>
          <IconBox>
            <Icon icon="far-microchip" size={16} color="gray12" />
          </IconBox>
          <MainInfo>
            <CardTitle>Custom Model Inference</CardTitle>
            <CardDescription>Host any custom model on GPU or CPU. Bring your own image.</CardDescription>
          </MainInfo>
        </Card>
        <Card onClick={() => window.open("https://docs.beam.cloud/v2/sandbox/overview", "_blank")}>
          <IconBox>
            <Icon icon="far-code" size={16} color="gray12" />
          </IconBox>
          <MainInfo>
            <CardTitle>Sandboxed Code Execution</CardTitle>
            <CardDescription>Run LLM-generated code in secure execution environments.</CardDescription>
          </MainInfo>
        </Card>
        <Card onClick={() => window.open("https://docs.beam.cloud/v2/function/running-functions", "_blank")}>
          <IconBox>
            <Icon icon="far-repeat" size={16} color="gray12" />
          </IconBox>
          <MainInfo>
            <CardTitle>Training & Fine-Tuning</CardTitle>
            <CardDescription>Train and fine-tune, from SLMs and LLMs to diffusion models.</CardDescription>
          </MainInfo>
        </Card>
        <Card onClick={() => window.open("https://docs.beam.cloud/v2/task-queue/running-tasks", "_blank")}>
          <IconBox>
            <Icon icon="far-bars-staggered" size={16} color="gray12" />
          </IconBox>
          <MainInfo>
            <CardTitle>Audio Processing Pipelines</CardTitle>
            <CardDescription>Deploy task queues to process large amounts of data.</CardDescription>
          </MainInfo>
        </Card>
        <Card onClick={() => window.open("https://docs.beam.cloud/v2/pod/web-service", "_blank")}>
          <IconBox>
            <Icon icon="far-rocket-launch" size={16} color="gray12" />
          </IconBox>
          <MainInfo>
            <CardTitle>Streamlit and Gradio UIs</CardTitle>
            <CardDescription>Run frontend apps, from Streamlit and Gradio apps to Notebooks.</CardDescription>
          </MainInfo>
        </Card>
        <Card onClick={() => window.open("https://docs.beam.cloud/v2/examples/web-scraping", "_blank")}>
          <IconBox>
            <Icon icon="far-box" size={16} color="gray12" />
          </IconBox>
          <MainInfo>
            <CardTitle>Web Scraping</CardTitle>
            <CardDescription>Run Chromium instances - headed or headless - at scale.</CardDescription>
          </MainInfo>
        </Card>
      </CardGrid>
    </Container>
  );
};

export default LaunchSection;

const Container = styled(SectionContainer)`
  ${({ theme }) => {
    return css`
      position: relative;
      z-index: 0;
      padding: 80px 16px;
      gap: 48px;

      @media (min-width: ${theme.breakpoints.sm}px) {
        padding: 96px 32px;
        gap: 64px;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        padding: 112px 48px;
        gap: 80px;
      }

      @media (min-width: ${theme.breakpoints.lg}px) {
        padding: 128px 32px;
        gap: 96px;
      }

      @media (min-width: ${theme.breakpoints.xl}px) {
        padding: 144px 64px;
        gap: 112px;
      }

      &:before {
        content: "";
        position: absolute;
        z-index: -1;
        right: 0;
        left: 0;
        margin: auto;
        height: 450px;
        width: 100%;
        max-width: 960px;
        min-width: 640px;
        background-image: url(${WorldMap});
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;

        bottom: -200px;

        @media (max-width: ${theme.breakpoints.xs}px) {
          background-size: contain;
          left: -25%;
          bottom: -150px;
        }
      }
    `;
  }}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 24px;
`;

const DocsButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledTitle = styled(SectionTitle)`
  max-width: 100%;
`;

const CardGrid = styled.div`
  ${({ theme }) => {
    return css`
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      width: 100%;
      max-width: 1152px;
      grid-gap: 24px;

      @media (max-width: ${theme.breakpoints.lg}px) {
        grid-template-columns: 1fr;
      }
    `;
  }}
`;

const Card = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      padding: 20px 24px;
      box-sizing: border-box;
      gap: 16px;
      background: ${theme.colors.gray2};
      border: 1px solid ${theme.colors.gray6};
      border-radius: 8px;
      width: 100%;
      cursor: pointer;
      transition: all 0.2s ease-in-out;

      &:hover {
        opacity: 0.8;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
    `;
  }}
`;

const MainInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 5px 0px 0px;
  gap: 8px;
`;

const CardTitle = styled.h5`
  ${({ theme }) => {
    return css`
      all: unset;
      color: ${theme.colors.gray12};
      ${typography.title20};

      @media (max-width: ${theme.breakpoints.sm}px) {
        ${typography.title18};
      }
    `;
  }}
`;

const CardDescription = styled.p`
  ${({ theme }) => {
    return css`
      all: unset;
      color: ${theme.colors.gray11};
      ${typography.textBaseLight};
    `;
  }}
`;

const IconBox = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding: 0px;
      isolation: isolate;
      box-sizing: border-box;
      white-space: nowrap;
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      background-color: ${theme.colors.gray4};
      border: 1px solid ${theme.colors.gray8};
      border-radius: 8px;
    `;
  }}
`;
